import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { SimpleModalComponent } from 'ngx-simple-modal';
import { GenerateInvoiceValidationErrorType } from 'src/app/core/enums/validation/generate-invoice-error-types.enum';

export interface FacilityMissingInvoicingGroupFacility {
	facilityId: number;
	facilityName?: string;
}

export interface FacilityMissingInvoicingGroupCompanyErrors {
	companyId: number;
	facilities: FacilityMissingInvoicingGroupFacility[];
}

export type FacilityMissingInvoicingGroupErrors = {
	[companyId: number]: { [errorId: number]: string };
};

@Component({
	selector: 'app-visit-facility-missing-invoicing-group',
	templateUrl: './visit-facility-missing-invoicing-group.component.html',
	styleUrls: ['./visit-facility-missing-invoicing-group.component.scss'],
})
export class VisitFacilityMissingInvoicingGroupComponent
	extends SimpleModalComponent<{ rawError: any }, void>
	implements OnInit
{
	readonly ErrorType = GenerateInvoiceValidationErrorType;
	readonly infoMessage =
		'Facility does not have link to invoicing group. Click a facility below to open its contract and fix the issue.';

	errors: FacilityMissingInvoicingGroupErrors = {};
	rawError: any;
	facilityErrors: FacilityMissingInvoicingGroupCompanyErrors[] = [];

	ngOnInit(): void {
		this.errors = this.extractFacilityMissingInvoicingGroupErrors(this.rawError);
		this.facilityErrors = this.buildFacilityErrors();
	}

	onClose(): void {
		location.reload();
	}

	hasFacilityErrors(): boolean {
		return this.facilityErrors.length > 0;
	}

	getFacilityDisplayName(error: FacilityMissingInvoicingGroupFacility): string {
		return error.facilityName ? `${error.facilityName}` : `ID #${error.facilityId}`;
	}

	getFacilityContractLink(facilityId: number): string {
		return `/facilities/${facilityId}/contracts`;
	}

	private buildFacilityErrors(): FacilityMissingInvoicingGroupCompanyErrors[] {
		if (!this.errors || typeof this.errors !== 'object') {
			return [];
		}

		return Object.entries(this.errors).reduce((acc, [companyKey, companyErrors]) => {
			const companyId = Number(companyKey);
			if (isNaN(companyId) || !companyErrors) {
				return acc;
			}

			const raw = companyErrors[this.ErrorType.FacilityMissingInvoicingGroup];
			const facilities = this.parseFacilityErrors(raw);

			if (facilities.length > 0) {
				acc.push({ companyId, facilities });
			}

			return acc;
		}, [] as FacilityMissingInvoicingGroupCompanyErrors[]);
	}

	private parseFacilityErrors(raw: unknown): FacilityMissingInvoicingGroupFacility[] {
		if (raw === undefined || raw === null) {
			return [];
		}

		const rawText = typeof raw === 'string' ? raw.trim() : JSON.stringify(raw);
		if (!rawText) {
			return [];
		}

		try {
			const parsed = JSON.parse(rawText);
			if (Array.isArray(parsed)) {
				return parsed
					.map(item => ({
						facilityId: Number(item?.facilityId ?? item?.FacilityId ?? item?.id ?? item?.Id ?? item),
						facilityName: item?.facilityName ?? item?.FacilityName ?? item?.name ?? item?.Name,
					}))
					.filter(item => !isNaN(item.facilityId) && item.facilityId > 0);
			}
		} catch (error) {
			const numericId = Number(rawText);
			if (!isNaN(numericId) && numericId > 0) {
				return [{ facilityId: numericId }];
			}
		}

		return [];
	}

	private extractFacilityMissingInvoicingGroupErrors(err: HttpErrorResponse): FacilityMissingInvoicingGroupErrors {
		const payload = this.getValidationErrorsPayload(err);
		if (!payload) {
			return {};
		}

		const firstParse = this.tryParseJson(payload);
		if (firstParse && typeof firstParse === 'object') {
			return firstParse as FacilityMissingInvoicingGroupErrors;
		}

		if (typeof firstParse === 'string') {
			const secondParse = this.tryParseJson(firstParse);
			if (secondParse && typeof secondParse === 'object') {
				return secondParse as FacilityMissingInvoicingGroupErrors;
			}
		}

		return {};
	}

	private getValidationErrorsPayload(err: HttpErrorResponse): string | null {
		if (!err || err.error === undefined || err.error === null) {
			return null;
		}

		if (typeof err.error === 'string') {
			return err.error;
		}

		if (typeof err.error.message === 'string') {
			return err.error.message;
		}

		return null;
	}

	private tryParseJson(value: string): unknown {
		try {
			return JSON.parse(value);
		} catch {
			return null;
		}
	}
}
